import { SLOT_BATTLE_NO_SEGMENT_VARIATION } from './constants';
import type { SlotBattle } from './types';

/**
 * Whether the run flag, as this client sees it, places the player in the battle's segment.
 *
 * Strict: only an exact match on the battle's segment key shows the battle. core creates run flags
 * client-side (SDKs using Client-side ID) and refuses segments the browser context cannot evaluate,
 * so the browser and core's server-side read agree on who is in which battle. Everything else hides:
 *
 *  - `filter`, or another segment's key: the player matches no battle of this run, or a different
 *    one — typically retargeted after the coin-scoped read was cached (core caches it ten minutes).
 *  - `undefined`: the flag is in neither the SDK's set nor the server-evaluated bootstrap — still
 *    loading, or a run flag that was never made client-side. Hiding keeps a battle from appearing
 *    and then vanishing; with a bootstrap the value is already there on the first render.
 */
export function isPlayerInBattleSegment(segmentFromFlag: string | undefined, segmentKey: string): boolean {
  return (
    segmentFromFlag !== undefined &&
    segmentFromFlag !== SLOT_BATTLE_NO_SEGMENT_VARIATION &&
    segmentFromFlag === segmentKey
  );
}

/**
 * Whether this battle may be shown. A settled closing skips the flag: core serves it from the
 * player's participant row, and the run flag may be off after a cancel or have drifted since, so
 * gating it would forfeit an unclaimed reward. Live and upcoming battles keep the strict gate.
 */
export function isBattleVisible(battle: SlotBattle, segmentFromFlag: string | undefined): boolean {
  if (battle.closing || battle.state === 'settled') {
    return true;
  }
  return isPlayerInBattleSegment(segmentFromFlag, battle.segmentKey);
}
